import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import cn from 'classnames';
import Decimal from 'decimal.js';
import { TextField, TextFieldWithDropdown, Typography, CloseButton } from 'seagull';
import { useLocaleKeys } from '~/i18n';
import { StagesTerms } from '~/models';
import { utils } from '~/utils';
import styles from './BillingStagesSelect.module.scss';
import { getHelperText, stagesTextsPath, valueTypeTextMap, isValueTypePercent, isValueTypeDollars } from './BillingStagesSelect.utils';
const formatMobileDisplayValue = (value, valueType)=>isValueTypeDollars(valueType) ? utils.formatDollarAmount(value) : "".concat(value, "%");
export const Stage = (param)=>{
    let { index, stage, isLast, isFirst, discount, totalRate, isAmendment, effectiveDate, disabledReason, stageClassName, discountedTotalRate, totalSumExceedsRate, oneTimeInStagesBilling, onRemove, onChange } = param;
    const { t: stagesTexts } = useLocaleKeys(stagesTextsPath);
    const valueType = oneTimeInStagesBilling.valueType();
    const validateStageValue = (value)=>{
        const tempStage = {
            stageNumber: stage.stageNumber,
            value: new Decimal(value || 0).toDecimalPlaces(2)
        };
        const text = getHelperText({
            stage: tempStage,
            isLast,
            valueType,
            totalRate,
            totalSumExceedsRate,
            texts: stagesTexts
        });
        return {
            error: !!text,
            helperText: text
        };
    };
    const { error: stageError, helperText } = validateStageValue(stage.value.toString());
    const stagedBillingBehaviour = oneTimeInStagesBilling.terms();
    const width = 216;
    const getBillingDescription = ()=>{
        if (!(isFirst && stagedBillingBehaviour === StagesTerms.AutoFirst)) {
            return stagesTexts.billingDescription.billedManually();
        }
        if (isAmendment) {
            return stagesTexts.billingDescription.billedAutomaticallyOnAmendmentsSaved();
        }
        return effectiveDate ? stagesTexts.billingDescription.billedAutomaticallyOnEffectiveDate() : stagesTexts.billingDescription.billedAutomaticallyOnApproval();
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: cn(styles.stage, stageClassName),
        children: [
            isFirst ? /*#__PURE__*/ _jsx(TextFieldWithDropdown, {
                bi: {
                    name: 'stage_value'
                },
                collapseHelperText: false,
                mobile: {
                    renderAsCard: true,
                    displayValue: formatMobileDisplayValue(stage.value, valueType)
                },
                width: width,
                minWidth: width,
                error: stageError,
                helperText: helperText,
                validateInBottomSheet: (text, option)=>{
                    const tempStage = {
                        stageNumber: stage.stageNumber,
                        value: new Decimal(text || 0).toDecimalPlaces(2)
                    };
                    const validationText = getHelperText({
                        stage: tempStage,
                        isLast,
                        valueType: option,
                        totalRate,
                        totalSumExceedsRate,
                        texts: stagesTexts
                    });
                    return {
                        error: !!validationText,
                        helperText: validationText
                    };
                },
                alwaysShrinkLabel: true,
                label: stagesTexts.stageLabel({
                    stageNumber: stage.stageNumber
                }),
                disabled: !!disabledReason,
                value: {
                    text: stage.value.toString(),
                    option: valueType
                },
                options: Object.entries(valueTypeTextMap).map((param)=>{
                    let [value, text] = param;
                    return {
                        value: value,
                        label: text
                    };
                }),
                deriveProps: (option)=>({
                        isMoney: isValueTypeDollars(option),
                        isPercentage: isValueTypePercent(option)
                    }),
                testId: "billing-stage-".concat(index),
                onChange: (v)=>{
                    const optionChanged = v.option !== valueType;
                    const textChanged = v.text !== stage.value.toString();
                    if (!optionChanged && !textChanged) return;
                    onChange(textChanged ? {
                        stageNumber: stage.stageNumber,
                        value: new Decimal(v.text || 0).toDecimalPlaces(2)
                    } : stage, optionChanged ? v.option : undefined);
                }
            }) : /*#__PURE__*/ _jsx(TextField, {
                bi: {
                    name: 'stage_value'
                },
                collapseHelperText: false,
                mobile: {
                    renderAsCard: true,
                    displayValue: formatMobileDisplayValue(stage.value, valueType)
                },
                width: width,
                minWidth: width,
                disableSuffixPadding: true,
                error: stageError,
                helperText: helperText,
                validateInBottomSheet: validateStageValue,
                alwaysShrinkLabel: true,
                label: stagesTexts.stageLabel({
                    stageNumber: stage.stageNumber
                }),
                disabled: isLast || !!disabledReason,
                isMoney: isValueTypeDollars(valueType),
                isPercentage: isValueTypePercent(valueType),
                value: stage.value.toString(),
                testId: "billing-stage-".concat(index),
                onChange: (e)=>{
                    onChange({
                        stageNumber: stage.stageNumber,
                        value: new Decimal(e.target.value || 0).toDecimalPlaces(2)
                    });
                },
                suffix: /*#__PURE__*/ _jsx(Typography, {
                    className: styles.valueTypeSelect,
                    testId: "billing-stage-".concat(index, "-split-by"),
                    children: valueTypeTextMap[valueType]
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                className: styles.closeButtonContainer,
                children: !isFirst && !isLast && !disabledReason && /*#__PURE__*/ _jsx(CloseButton, {
                    bi: {
                        name: 'remove_stage'
                    },
                    skin: "transparent",
                    testId: "billing-stage-".concat(index, "-remove"),
                    onClick: onRemove
                })
            }),
            /*#__PURE__*/ _jsx(Typography, {
                skin: "secondary",
                className: styles.stageDescription,
                testId: "billing-stage-".concat(index, "-description"),
                children: "".concat(isValueTypeDollars(valueType) ? !discount.isZero() ? utils.formatDollarAmount(stage.value.mul(new Decimal(1).minus(discount.div(100)))) : '' : utils.formatDollarAmount(stage.value.div(100).mul(discountedTotalRate)), " ").concat(getBillingDescription())
            })
        ]
    });
};
